import { useEffect, useMemo, useState } from "react";
import type { Column } from "@ir/ui";

const STORAGE_PREFIX = "ir-admin-visible-columns:";
const DEFAULT_ALWAYS_VISIBLE_COLUMNS = ["actions"];

function sameStringList(left: string[], right: string[]) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function safeRead(storageKey: string): string[] | null {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) ?? "null");
    return Array.isArray(parsed) && parsed.every((item) => typeof item === "string")
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function safeWrite(storageKey: string, value: string[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Column visibility is a preference only; private-mode storage failures
    // must not break the operational tables.
  }
}

export function useColumnVisibility<T>(
  tableId: string,
  columns: Column<T>[],
  defaultVisibleIds: string[],
  alwaysVisibleIds: string[] = DEFAULT_ALWAYS_VISIBLE_COLUMNS,
) {
  const storageKey = `${STORAGE_PREFIX}${tableId}`;
  const selectableIds = useMemo(
    () => columns.map((column) => column.id).filter((id) => !alwaysVisibleIds.includes(id)),
    [alwaysVisibleIds, columns],
  );

  const [visibleIds, setVisibleIds] = useState<string[]>(() => {
    const stored = safeRead(storageKey);
    const allowed = new Set(selectableIds);
    const validStored = stored?.filter((id) => allowed.has(id)) ?? [];
    const defaults = defaultVisibleIds.filter((id) => allowed.has(id));
    return validStored.length > 0 ? validStored : defaults;
  });

  useEffect(() => {
    setVisibleIds((current) => {
      const allowed = new Set(selectableIds);
      const next = current.filter((id) => allowed.has(id));
      if (next.length > 0) {
        return sameStringList(current, next) ? current : next;
      }

      const defaults = defaultVisibleIds.filter((id) => allowed.has(id));
      return sameStringList(current, defaults) ? current : defaults;
    });
  }, [defaultVisibleIds, selectableIds]);

  useEffect(() => {
    safeWrite(storageKey, visibleIds);
  }, [storageKey, visibleIds]);

  const visibleColumns = useMemo(() => {
    const visible = new Set([...visibleIds, ...alwaysVisibleIds]);
    return columns.filter((column) => visible.has(column.id));
  }, [alwaysVisibleIds, columns, visibleIds]);

  const toggleColumn = (id: string) => {
    setVisibleIds((current) => {
      if (current.includes(id)) {
        const next = current.filter((value) => value !== id);
        return next.length > 0 ? next : current;
      }
      return [...current, id];
    });
  };

  const resetColumns = () => {
    setVisibleIds(defaultVisibleIds.filter((id) => selectableIds.includes(id)));
  };

  return {
    selectableColumns: columns.filter((column) => selectableIds.includes(column.id)),
    visibleColumnIds: visibleIds,
    visibleColumns,
    toggleColumn,
    resetColumns,
    minimumReached: visibleIds.length <= 1,
  };
}
